import { SupervisorApi } from "apis";
import ActionCreator from "./common";
import { ActionTypes } from "store/actionTypes";
import { AppDispatch, RootState } from "store";

class SupervisorAction extends ActionCreator<SupervisorApi> {
  private Service;
  private listReadSequence = 0;
  private detailReadSequence = 0;
  private activeDetailOptions: { ticketId: string; includeSystemNotes?: boolean } | null = null;
  public cancelListReads = () => { this.listReadSequence++; };
  public cancelDetailReads = () => { this.detailReadSequence++; };

  constructor() {
    super(
      SupervisorApi,
      ActionTypes.SUPERVISOR_LOADING,
      ActionTypes.SUPERVISOR_ERROR
    );

    this.Service = new SupervisorApi();
  }

  public getSuperopsTickets = (
    page: number,
    pageSize: number,
    filters: {
      groupName?: string;
      categoryName?: string[];
      status?: string[];
      customer?: string[];
      assignedTo?: string;
      includeAllStatuses?: boolean;
      refreshToken?: string;
    },
    sort: { attribute: string; order: "ASC" | "DESC" }[],
    options: { silent?: boolean } = {}
  ) => {
    return async (dispatch: AppDispatch, getState: RootState) => {
      const sequence = ++this.listReadSequence;
      const publish = (response: any) => {
        if (sequence !== this.listReadSequence) return;
        dispatch({ type: ActionTypes.GET_TICKETS, payload: response, context: "superops" });
      };
      if (!options.silent) dispatch(this.loading());
      try {
        const response = await this.Service.getSuperopsTickets(
          page,
          pageSize,
          {
            groupName: filters.groupName,
            categoryName: filters.categoryName,
            status: filters.status,
            customer: filters.customer,
            assignedTo: filters.assignedTo,
            includeAllStatuses: filters.includeAllStatuses,
            refreshToken: filters.refreshToken,
          },
          sort,
          publish
        );
        publish(response);
      } catch (error) {
        if (sequence === this.listReadSequence && !options.silent) dispatch(this.error(error));
      }
    };
  };

  public getNinjaoneTickets = (
    page: number,
    pageSize: number,
    filters: {
      groupName?: string;
      categoryName?: string[];
      status?: string[];
      boardId?: number;
      assignedTo?: string;
      includeAllStatuses?: boolean;
      refreshToken?: string;
    },
    sort: { attribute: string; order: "ASC" | "DESC" }[],
    options: { silent?: boolean } = {}
  ) => {
    return async (dispatch: AppDispatch, getState: RootState) => {
      const sequence = ++this.listReadSequence;
      const publish = (response: any) => {
        if (sequence !== this.listReadSequence) return;
        dispatch({ type: ActionTypes.GET_TICKETS, payload: response, context: "ninjaone" });
      };
      if (!options.silent) dispatch(this.loading());
      try {
        const response = await this.Service.getNinjaoneTickets(
          page,
          pageSize,
          {
            groupName: filters.groupName,
            categoryName: filters.categoryName,
            status: filters.status,
            boardId: filters.boardId,
            assignedTo: filters.assignedTo,
            includeAllStatuses: filters.includeAllStatuses,
            refreshToken: filters.refreshToken,
          },
          sort,
          publish
        );
        publish(response);
      } catch (error) {
        if (sequence === this.listReadSequence && !options.silent) dispatch(this.error(error));
      }
    };
  };

  public getAutotaskTickets = (
    page: number,
    pageSize: number,
    filters: {
      groupName?: string;
      categoryName?: string[];
      status?: string[];
      customer?: string[];
      assignedTo?: string;
      includeAllStatuses?: boolean;
      refreshToken?: string;
    },
    sort: { attribute: string; order: "ASC" | "DESC" }[],
    options: { silent?: boolean } = {}
  ) => {
    return async (dispatch: AppDispatch, getState: RootState) => {
      const sequence = ++this.listReadSequence;
      const publish = (response: any) => {
        if (sequence !== this.listReadSequence) return;
        dispatch({ type: ActionTypes.GET_TICKETS, payload: response, context: "autotask" });
      };
      if (!options.silent) dispatch(this.loading());
      try {
        const response = await this.Service.getAutotaskTickets(
          page,
          pageSize,
          {
            groupName: filters.groupName,
            categoryName: filters.categoryName,
            status: filters.status,
            customer: filters.customer,
            assignedTo: filters.assignedTo,
            includeAllStatuses: filters.includeAllStatuses,
            refreshToken: filters.refreshToken,
          },
          sort,
          publish
        );
        publish(response);
      } catch (error) {
        if (sequence === this.listReadSequence && !options.silent) dispatch(this.error(error));
      }
    };
  };

  public getHaloTickets = (
    page: number,
    pageSize: number,
    filters: {
      groupName?: string;
      categoryName?: string[];
      status?: string[];
      customer?: string[];
      assignedTo?: string;
      includeAllStatuses?: boolean;
      refreshToken?: string;
    },
    sort: { attribute: string; order: "ASC" | "DESC" }[],
    options: { silent?: boolean } = {}
  ) => {
    return async (dispatch: AppDispatch, getState: RootState) => {
      const sequence = ++this.listReadSequence;
      const publish = (response: any) => {
        if (sequence !== this.listReadSequence) return;
        dispatch({ type: ActionTypes.GET_TICKETS, payload: response, context: "halo" });
      };
      if (!options.silent) dispatch(this.loading());
      try {
        const response = await this.Service.getSuperopsTickets(
          page,
          pageSize,
          {
            groupName: filters.groupName,
            categoryName: filters.categoryName,
            status: filters.status,
            customer: filters.customer,
            assignedTo: filters.assignedTo,
            includeAllStatuses: filters.includeAllStatuses,
            refreshToken: filters.refreshToken,
          },
          sort,
          publish
        );
        publish(response);
      } catch (error) {
        if (sequence === this.listReadSequence && !options.silent) dispatch(this.error(error));
      }
    };
  };

  public getTicketById = (
    ticketId: string,
    email?: string,
    options?: { includeSystemNotes?: boolean; background?: boolean }
  ) => {
    return async (dispatch: AppDispatch, getState: RootState) => {
      if (!options?.background) dispatch(this.loading());
      if (!options?.background) this.activeDetailOptions = { ticketId, includeSystemNotes: options?.includeSystemNotes };
      const detailOptions = options?.background && this.activeDetailOptions?.ticketId === ticketId
        ? { ...options, includeSystemNotes: this.activeDetailOptions.includeSystemNotes } : options;
      const sequence = ++this.detailReadSequence;
      const publish = (response: any) => {
        if (sequence === this.detailReadSequence) dispatch({ type: ActionTypes.GET_TICKET_BY_ID, payload: response });
      };
      try {
        const response = await this.Service.getTicketById(ticketId, email, detailOptions, publish);
        publish(response);
      } catch (error) {
        if (sequence === this.detailReadSequence && !options?.background) dispatch(this.error(error));
      }
    };
  };

  // Silent refresh - updates ticket data without triggering global loading state
  public refreshTicketById = (
    ticketId: string,
    email?: string,
    options?: { includeSystemNotes?: boolean; background?: boolean }
  ) => {
    return async (dispatch: AppDispatch, getState: RootState) => {
      if (!options?.background) this.activeDetailOptions = { ticketId, includeSystemNotes: options?.includeSystemNotes };
      const detailOptions = options?.background && this.activeDetailOptions?.ticketId === ticketId
        ? { ...options, includeSystemNotes: this.activeDetailOptions.includeSystemNotes } : options;
      const sequence = ++this.detailReadSequence;
      const publish = (response: any) => {
        if (sequence === this.detailReadSequence) dispatch({ type: ActionTypes.GET_TICKET_BY_ID, payload: response });
      };
      try {
        const response = await this.Service.getTicketById(ticketId, email, { ...detailOptions, forceRefresh: !options?.background }, publish);
        publish(response);
      } catch (error) {
        // Silently fail - don't show error for background refresh
        console.error("Failed to refresh ticket:", error);
      }
    };
  };

  public getTechnicianGroupList = (email?: string) => {
    return async (dispatch: AppDispatch, getState: RootState) => {
      dispatch(this.loading());
      try {
        const response = await this.Service.getTechnicianGroupList(email);
        dispatch({
          type: ActionTypes.GET_TECHNICIAN_GROUP_LIST,
          payload: response,
        });
      } catch (error) {
        dispatch({
          type: ActionTypes.GET_TECHNICIAN_GROUP_LIST,
          payload: [],
        });
        dispatch(this.error(error));
      }
    };
  };

  public getTechnicianList = () => {
    return async (dispatch: AppDispatch, getState: RootState) => {
      dispatch(this.loading());
      try {
        const response = await this.Service.getTechnicianList();
        dispatch({
          type: ActionTypes.GET_TECHNICIAN_LIST,
          payload: response,
        });
      } catch (error) {
        dispatch(this.error(error));
      }
    };
  };

  public getNinjaoneBoardList = () => {
    return async (dispatch: AppDispatch, getState: RootState) => {
      dispatch(this.loading());
      try {
        const response = await this.Service.get("/boards");
        dispatch({
          type: ActionTypes.GET_NINJAONE_BOARD_LIST,
          payload: response,
        });
      } catch (error) {
        dispatch({
          type: ActionTypes.GET_NINJAONE_BOARD_LIST,
          payload: [],
        });
        dispatch(this.error(error));
      }
    };
  };

  public getNinjaoneUsers = () => {
    return async (dispatch: AppDispatch, getState: RootState) => {
      dispatch(this.loading());
      try {
        const response = await this.Service.getNinjaoneUsers();
        dispatch({
          type: ActionTypes.GET_NINJAONE_USERS,
          payload: response,
        });
      } catch (error) {
        dispatch(this.error(error));
      }
    };
  };

  public submitFeedback = (feedbackData: {
    type: 'positive' | 'negative';
    ticketId: string;
    url: string;
    comment?: string;
    context?: any;
  }) => {
    return async (dispatch: AppDispatch) => {
      try {
        const response = await this.Service.submitFeedback(feedbackData);
        return response;
      } catch (error) {
        console.error('Feedback submission error:', error);
        throw error;
      }
    };
  };

  public initialize = () => ({
    type: ActionTypes.AUTH_INITIALIZE,
  });
}

export default new SupervisorAction();
